import type { SessionLite } from '../../../../../types/session-lite';


/**
 * buildTourSessions returns the session list that should be rendered in the
 * timeline during each onboarding tour step. Steps 2-6 inject mock sessions so
 * the tour can highlight specific UI states (past, next, cancelled) regardless
 * of what is actually loaded for the selected day.
 *
 * @param tourStepIndex - Current step index (-1 means tour is not active).
 * @param selectedISO   - ISO date of the currently selected day.
 * @param realSessions  - The actual sessions fetched for the selected day.
 */
export function buildTourSessions(
  tourStepIndex: number,
  selectedISO: string,
  realSessions: SessionLite[],
): SessionLite[] {
  if (tourStepIndex < 0) return realSessions;

  const makeISO = (isoDate: string, time: string) => `${isoDate}T${time}:00.000Z`;
  const toISO = (d: Date) => d.toISOString().split('T')[0];
  const today = new Date();
  const yesterdayISO = toISO(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1));
  const tomorrowISO = toISO(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));

  const pastMock = {
    id: 'tour-past',
    date: makeISO(yesterdayISO, '10:00'),
    patientName: 'Juan Suarez',
    therapyType: 'Individual',
    isCancelled: false,
    status: 'settled',
  } as unknown as SessionLite;

  const nextMock = {
    id: 'tour-next',
    date: makeISO(tomorrowISO, '10:00'),
    patientName: 'Jorge Perez',
    therapyType: 'Individual',
    isCancelled: false,
    status: 'active',
    isFirst: true,
    isNext: true,
    joinable: true,
  } as unknown as SessionLite;

  const cancelledMock = {
    id: 'tour-cancelled',
    date: makeISO(selectedISO, '12:00'),
    patientName: 'Marina Gomez',
    therapyType: 'Individual',
    isCancelled: true,
    status: 'cancelled',
  } as unknown as SessionLite;

  switch (tourStepIndex) {
    case 1: return [];
    case 2: return [pastMock];
    case 3: return [nextMock];
    case 4: return [nextMock]; // chips step
    case 5: return [nextMock]; // actions step
    case 6: return [cancelledMock];
    default: return realSessions;
  }
}
